import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Tables } from "@/integrations/supabase/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoreHorizontal, Eye, ExternalLink } from "lucide-react";
import { ApplicationDetailsDialog } from "./ApplicationDetailsDialog";

type JobApplication = Tables<"job_applications"> & {
  job_postings: { title: string; department: string } | null;
};

interface JobApplicationsTableProps {
  jobApplications: JobApplication[];
  isLoading: boolean;
}

const statusColors = {
  submitted: "default",
  reviewing: "secondary",
  interviewing: "outline",
  rejected: "destructive",
  accepted: "default",
} as const;

export const JobApplicationsTable = ({ jobApplications, isLoading }: JobApplicationsTableProps) => {
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const { toast } = useToast();

  const updateApplicationStatus = async (applicationId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('job_applications')
        .update({ status: newStatus })
        .eq('id', applicationId);

      if (error) throw error;

      toast({
        title: "Status updated",
        description: "Application status updated successfully.",
      });

      // You might want to trigger a refetch here
    } catch (error) {
      console.error('Error updating application status:', error);
      toast({
        title: "Error",
        description: "Failed to update status. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading applications...</div>;
  }

  if (!jobApplications.length) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No applications found</p>
        <p className="text-sm text-muted-foreground">Applications will appear here when candidates apply for your job postings.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Applicant</TableHead>
              <TableHead>Job Position</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Applied</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobApplications.map((application) => (
              <TableRow 
                key={application.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setSelectedApplication(application)}
              >
                <TableCell className="font-medium">
                  {application.first_name} {application.last_name}
                </TableCell>
                <TableCell>{application.job_postings?.title || "Unknown Position"}</TableCell>
                <TableCell>{application.job_postings?.department || "Unknown"}</TableCell>
                <TableCell>{application.email}</TableCell>
                <TableCell>{application.phone || "-"}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Select
                    value={application.status || "submitted"}
                    onValueChange={(value) => updateApplicationStatus(application.id, value)}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="reviewing">Reviewing</SelectItem>
                      <SelectItem value="interviewing">Interviewing</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {application.applied_at 
                    ? formatDistanceToNow(new Date(application.applied_at), { addSuffix: true })
                    : "Unknown"
                  }
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setSelectedApplication(application)}>
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      {application.resume_url && (
                        <DropdownMenuItem asChild>
                          <a href={application.resume_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Download Resume
                          </a>
                        </DropdownMenuItem>
                      )}
                      {application.linkedin_url && (
                        <DropdownMenuItem asChild>
                          <a href={application.linkedin_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            View LinkedIn
                          </a>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedApplication && (
        <ApplicationDetailsDialog
          application={selectedApplication}
          isOpen={!!selectedApplication}
          onClose={() => setSelectedApplication(null)}
        />
      )}
    </>
  );
};